/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'

import type { EuiTableComputedColumnType } from '@elastic/eui'
import { EuiFlexGroup, EuiFlexItem, EuiLink, EuiTextColor } from '@elastic/eui'

import type { AdminConsoleCCMCluster, CCMService } from '@modules/ccm-admin-lib/types'
import { isDisconnectedCluster } from '@modules/ccm-admin-lib/util'
import { connectedClusterURL, getLinkedServiceUrl } from '@modules/ccm-lib/urls'
import { getServicesForCluster } from '@modules/ccm-lib/util'
import { CuiCombinedTime } from '@modules/cui/TimeAgo'
import { FormattedMessage } from '@modules/i18n'
import { organizationOverviewUrl } from '@modules/urls/organizations'
import { userOverviewUrl } from '@modules/urls/users'

import CCMClusterLicenseTypeBadge from './CCMClusterLicenseTypeBadge'
import ClusterIdTableCell from './ClusterIdTableCell'
import ColumnTextColorWrapper from './ColumnTextColorWrapper'
import messages from './messages'

import type { FC } from 'react'
import type { CCMClusterTableColumns } from './types'

const serviceNameMessages = defineMessages<CCMService>({
  auto_ops: {
    id: 'ccm.clusters-table.service.autoops',
    defaultMessage: 'AutoOps',
  },
  eis: {
    id: 'ccm.clusters-table.service.eis',
    defaultMessage: 'EIS',
  },
})

export const useTableColumns = (
  visibleColumns?: CCMClusterTableColumns[],
): Array<EuiTableComputedColumnType<AdminConsoleCCMCluster>> => {
  const { formatMessage } = useIntl()

  const navigate = useNavigate()

  const columns: Array<
    EuiTableComputedColumnType<AdminConsoleCCMCluster> & { key?: CCMClusterTableColumns }
  > = [
    {
      key: 'id',
      name: formatMessage(messages.clusterId),
      render: (cluster: AdminConsoleCCMCluster) => <ClusterIdTableCell cluster={cluster} />,
    },
    {
      key: 'organizationId',
      name: formatMessage(messages.organizationId),
      render: (cluster: AdminConsoleCCMCluster) => (
        <ColumnTextColorWrapper ccmCluster={cluster}>
          <EuiLink
            onClick={() => navigate(organizationOverviewUrl(cluster.metadata.organization_id))}
          >
            {cluster.metadata.organization_id}
          </EuiLink>
        </ColumnTextColorWrapper>
      ),
    },
    {
      key: 'licenseId',
      name: formatMessage(messages.license),
      render: (cluster: AdminConsoleCCMCluster) => (
        <EuiFlexGroup direction='column' gutterSize='s'>
          <EuiFlexItem>
            <ColumnTextColorWrapper ccmCluster={cluster}>
              {cluster.license.uid}
            </ColumnTextColorWrapper>
          </EuiFlexItem>
          <div>
            <CCMClusterLicenseTypeBadge licenseType={cluster.license.type} />
          </div>
        </EuiFlexGroup>
      ),
    },
    {
      key: 'services',
      name: formatMessage(messages.services),
      render: (cluster: AdminConsoleCCMCluster) => {
        const Wrapper: FC<{ service: CCMService }> = ({ service, children }) => {
          if (isDisconnectedCluster(cluster)) {
            return <EuiTextColor color='subdued'>{children}</EuiTextColor>
          }

          const href = getLinkedServiceUrl(service, cluster)

          if (href !== undefined) {
            return (
              <EuiLink href={href} key={service}>
                {children}
              </EuiLink>
            )
          }

          return <Fragment>{children}</Fragment>
        }

        const services = getServicesForCluster(cluster)

        return services.map((service) => (
          <Wrapper key={service} service={service}>
            {formatMessage(serviceNameMessages[service])}
          </Wrapper>
        ))
      },
    },
    {
      key: 'connectedBy',
      name: formatMessage(messages.connectedBy),
      render: (ccmCluster: AdminConsoleCCMCluster) => (
        <ColumnTextColorWrapper ccmCluster={ccmCluster}>
          <EuiLink onClick={() => navigate(userOverviewUrl(ccmCluster.metadata.created_by))}>
            {ccmCluster.metadata.created_by}
          </EuiLink>
        </ColumnTextColorWrapper>
      ),
    },
    {
      key: 'connectedAt',
      name: formatMessage(messages.connectedAt),
      render: (ccmCluster: AdminConsoleCCMCluster) => (
        <ColumnTextColorWrapper ccmCluster={ccmCluster}>
          <CuiCombinedTime date={new Date(ccmCluster.metadata.created_at)} />
        </ColumnTextColorWrapper>
      ),
    },
    {
      name: formatMessage(messages.actions),
      render: (ccmCluster: AdminConsoleCCMCluster) => {
        if (isDisconnectedCluster(ccmCluster)) {
          return null
        }

        return (
          <EuiLink onClick={() => navigate(connectedClusterURL(ccmCluster.id))}>
            <FormattedMessage id='ccm.clusters-table.actions.manage' defaultMessage='Manage' />
          </EuiLink>
        )
      },
      width: '6%',
    },
  ]

  return columns.filter(
    (column) =>
      visibleColumns === undefined ||
      column.key === undefined ||
      visibleColumns.includes(column.key as CCMClusterTableColumns),
  )
}
